/**
 * The rider dash home.
 *
 * This is a glanceable summary, not a work surface: whether you are online, how
 * the day is going, and the single next thing to do. The offers queue and the
 * accept/skip actions live on Jobs, so a rider cannot be looking at two lists
 * of the same offers in two places.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MinimalAlert,
  MinimalButton,
  MinimalCard,
  MinimalEmptyState,
  MinimalInfoPanel,
  MinimalStatCard,
} from '@ovasabi/ui-minimal'
import {
  BoltIcon,
  ClockIcon,
  RouteIcon,
  WalletIcon,
} from '../../components/icons'
import { Money, PageHeader, Screen } from '../../components/chow'
import {
  useDeliverySnapshot,
  useOfferSnapshot,
  useRiderLocationSnapshot,
} from '../../generated/prototypeRuntime'
import { commands, isLiveRuntime } from '../../lib/runtime'
import { dashSession, dashStats } from './demoData'
import { EmergencySos } from './EmergencySos'
import {
  activeDelivery,
  getStore,
  number,
  routeLine,
  text,
  useRiderId,
  type Snapshot,
} from './records'
import { riderStyle as Style } from './riderStyle'

export const HomePage = () => {
  const navigate = useNavigate()
  const riderId = useRiderId()
  const deliveries = useDeliverySnapshot() as Snapshot
  const offers = useOfferSnapshot() as Snapshot
  const locations = useRiderLocationSnapshot() as Snapshot
  const [online, setOnline] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const location = locations.records[0] ?? {}
  const active = activeDelivery(deliveries.records)

  const toggleDash = async () => {
    if (busy) return
    setBusy(true)
    setError('')
    try {
      const latitude = number(location, ['latitude'], 6.5244)
      const longitude = number(location, ['longitude'], 3.3792)
      const sessionId = text(location, ['dash_session_id'], 'dash-preview')
      if (online) {
        if (isLiveRuntime()) await commands.rider.stopDash(riderId, sessionId)
        else void commands.rider.stopDash(riderId, sessionId).catch(() => undefined)
      } else if (isLiveRuntime()) {
        await commands.rider.startDash(riderId, latitude, longitude)
      } else {
        void commands.rider.startDash(riderId, latitude, longitude).catch(() => undefined)
      }
      if (!isLiveRuntime() && location.id) {
        getStore('logistics.rider_locations')?.upsert({
          ...location,
          status: online ? 'offline' : 'online',
          created_at: new Date().toISOString(),
        })
      }
      setOnline(!online)
    } catch {
      setError('We could not change your dash status. Check your connection and try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen header={<PageHeader wordmark brandMark />}>
      {error ? <MinimalAlert tone="danger" title="Dash update">{error}</MinimalAlert> : null}

      <MinimalInfoPanel
        tone={online ? 'success' : 'warning'}
        layout="split"
        icon={<RouteIcon />}
        eyebrow={online ? 'Dash session · online' : 'Dash session · offline'}
        title={dashSession.zone}
        description={
          online
            ? `${dashSession.demand} — ${dashSession.nextPayout}`
            : 'Go online when you are ready to receive nearby offers.'
        }
        action={
          <Style.InlineActions>
            <MinimalButton variant="secondary" size="sm" loading={busy} onClick={toggleDash}>
              {online ? 'Go offline' : 'Go online'}
            </MinimalButton>
            {/* Reachable from here in one tap: an SOS behind a navigation step is
                not an SOS. See EmergencySos. */}
            <EmergencySos size="sm" />
          </Style.InlineActions>
        }
      />

      <Style.StatRow>
        {dashStats.map((stat) => (
          <Style.Cell key={stat.label}>
            <MinimalStatCard tone={stat.tone} label={stat.label} value={stat.value} hint={stat.hint} icon={<WalletIcon size="sm" />} />
          </Style.Cell>
        ))}
      </Style.StatRow>

      <Style.SectionTitle><BoltIcon />Next job</Style.SectionTitle>

      {active ? (
        <MinimalCard padding="md" hoverable>
          <Style.Stack>
            <Style.Top>
              <Style.Stack>
                <Style.Title>{text(active, ['merchant_display_name', 'restaurant_name'], 'Pickup')}</Style.Title>
                <Style.Meta>{routeLine(active)}</Style.Meta>
              </Style.Stack>
              <Money amount={number(active, ['pay_total_minor']) / 100} tone="brand" />
            </Style.Top>
<Style.Facts>
              <Style.Fact><RouteIcon size="sm" />Delivery in progress</Style.Fact>
              <Style.Fact><ClockIcon size="sm" />Confirm each hand-off in order</Style.Fact>
            </Style.Facts>
            <Style.FullAction>
              <MinimalButton onClick={() => navigate('/jobs')}>Open your job</MinimalButton>
            </Style.FullAction>
          </Style.Stack>
        </MinimalCard>
      ) : !online ? (
        <MinimalEmptyState
          icon={<BoltIcon />}
          title="You are offline"
          description="Go online to receive offers matched to your location and vehicle."
          action={<MinimalButton onClick={toggleDash}>Go online</MinimalButton>}
        />
      ) : offers.records.length === 0 ? (
        <MinimalEmptyState
          icon={<ClockIcon />}
          title="Looking for nearby orders"
          description="Keep ChowDash open. A route and earning estimate will appear before you decide."
          action={<MinimalButton variant="secondary" onClick={() => navigate('/jobs')}>Open jobs</MinimalButton>}
        />
      ) : (
        <MinimalCard padding="md" hoverable>
          <Style.Stack>
            <Style.Title>
              {offers.records.length} {offers.records.length === 1 ? 'offer' : 'offers'} ready
            </Style.Title>
            <Style.Meta>Route and earning are shown on each offer before you accept.</Style.Meta>
            <Style.FullAction>
              <MinimalButton onClick={() => navigate('/jobs')}>Review offers</MinimalButton>
            </Style.FullAction>
          </Style.Stack>
        </MinimalCard>
      )}
    </Screen>
  )
}
